/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * <p>
 * Copyright © 2019 Pearson Education, Inc.
 * All Rights Reserved.
 * <p>
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained from Pearson Education, Inc.
 *
 * This is the data class for holding partner attributes.
 *
 * @author urahish
 * 10/15/2019
 *
 */

import React, {useEffect, useRef, useState} from 'react';
import './table.css';
import './reactTableStyles.css';
import Checkbox from '../forms/form-elements/checkbox/checkbox';
import ReactTable from 'react-table';
import Icon from "../icon/Icon";
import API from "../../../utils/api";
import EventEmitter from "../../../eventEmmiters/EventEmitter";

const resolvePageSize = (tableConfig) => {
    if (tableConfig.pageSize) {
        return tableConfig.pageSize;
    }
    const match = tableConfig.tableBaseUrl && tableConfig.tableBaseUrl.match(/per-page=(\d+)/i);
    return match ? parseInt(match[1], 10) : 10;
};

/**
 * This is a generalized table. Add the app-properties details to the tableConfigs.js file
 *
 * @props
 *      tableConfig - table configurations
 *      routerProps - routing props
 *
 * @returns a table component
 */
const Table = (props) => {
    // get the user details
    const user = JSON.parse(localStorage.getItem('tmcUser'));

    // set the token
    const token = 'Bearer ' + user.token;

    // get table configs based on the path
    const table = props.tableConfig;
    const pageSize = resolvePageSize(table);

    // set currently visible table data
    const [tableData, setTableData] = useState([]);

    // set table columns
    const [tableColumns, setTableColumns] = useState(setInitialTableColumns(table));

    // set checked boxes with checked columns
    const [tableCheckedItems, setTableCheckedItems] = useState(setInitialCheckedItems(table));

    //set table loader
    const [tableLoader, setTableLoader] = useState(false);

    // set total page count in the pagination
    const [totalPageCount, setTotalPageCount] = useState(0);

    const [tableFilterUrl, setTableFilterUrl] = useState('');

    const isMounted = useRef(false);

    const reactTable = useRef();

    useEffect(() => {
        // execute during the initial load
        if (!isMounted.current) {
            EventEmitter.on('tableFilterUrl', handleTableFilterUrl)
        }
    }, [])

    useEffect(() => {
        if (isMounted.current) {
            console.log('useEffect tableFilterUrl');
            if (reactTable.current) {
                if (typeof reactTable.current.showPage === 'function') {
                    reactTable.current.showPage(0);
                }
                if (typeof reactTable.current.fireFetchData === 'function') {
                    reactTable.current.fireFetchData();
                }
            }
        } else {
            isMounted.current = true;
        }
    }, [tableFilterUrl])

    const handleTableFilterUrl = (url) => {
        setTableFilterUrl(url);
    }

    const handleChange = (e) => {
        const item = e.target.name;
        const isChecked = e.target.checked;

        const existingTableColumns = [...tableColumns];
        existingTableColumns.map(o => {
            if (o.Header === item)
                o.show = isChecked;
        });
        setTableColumns(existingTableColumns);

        setTableCheckedItems({...tableCheckedItems, [e.target.name] : e.target.checked });
    };

    // initialize table columns
    const tableTitleCheckBoxes = [];

    tableTitleCheckBoxes.push(
        tableColumns.map(item => (
            <fieldset key={item.Header}>
                <label className="regular-checkbox">
                    <Checkbox name={item.Header} checked={tableCheckedItems[item.Header]} onChange={handleChange}/>
                    {item.Header}
                </label>
            </fieldset>
        ))
    );

    EventEmitter.emit('tableColumnViewerData', {
        type: 'manageColumn',
        data: tableTitleCheckBoxes
    });

    const fetchTableData = (dataObjectList) => {
        const structuredTableData = [];
        if (dataObjectList.length !== 0) {
            dataObjectList.forEach(p => {
                const newData = Object.assign({}, p);

                // display icon in enabled field in partner table
                if (newData.enabled !== undefined) {
                    if (newData.enabled || newData.enabled === 1) {
                        newData.enabled = <Icon name="success-18" />;
                    } else {
                        newData.enabled = "";
                    }
                }

                // display icon in schoolAware field in institution
                if (newData.schoolAware !== undefined) {
                    if (newData.schoolAware || newData.enabled === 1) {
                        newData.schoolAware = <Icon name="success-18" />;
                    } else {
                        newData.schoolAware = "";
                    }
                }

                // display icon in schoolAware field in institution
                if (newData.masterConsent !== undefined) {
                    if (newData.masterConsent || newData.enabled === 1) {
                        newData.masterConsent = <Icon name="success-18" />;
                    } else {
                        newData.masterConsent = "";
                    }
                }

                // display roles in Accounts feature
                if (newData.accountAdminRoles !== undefined) {
                    let roles = "[ ";
                    if (newData.accountAdminRoles.length !== 0) {
                        newData.accountAdminRoles.forEach(role => {
                            roles = roles + role.adminRoleAuthority + " | ";
                        });
                    }
                    roles = roles.slice(0, -3) + " ]";
                    newData.accountAdminRoles = roles;
                }

                // display status in tool providers table
                if (newData.toolStatusUp !== undefined) {
                    if (newData.toolStatusUp) {
                        newData.toolStatusUp = "Running";
                    } else {
                        newData.toolStatusUp = "Stopped";
                    }
                }

                //
                if (newData.courseCreationBlocked !== undefined) {
                    if (newData.enabled || newData.courseCreationBlocked === true) {
                        newData.courseCreationBlocked = "Yes";
                    } else {
                        newData.courseCreationBlocked = "No";
                    }
                }

                structuredTableData.push(newData);
            });
            return structuredTableData;
        }
    }

    const onFetchData = async (state) => {
        const tableBaseUrl = table.tableBaseUrl;
        console.log("state",state);
        if (state.sorted.length !== undefined && state.sorted.length !== 0) {
            if (state.filtered.length !== undefined && state.filtered.length !== 0) {
                console.log('both search = ' + state.filtered[0]['id'] + '& sort = ' + state.sorted[0]['id']);

                setTableLoader(true);

                let sortOrder = 'ASC';
                if (state.sorted[0]['desc'])
                    sortOrder = 'DESC';

                let url = tableBaseUrl + state.page + '&search=%7B';

                state.filtered.forEach(param => {
                    url += param['id'] + ':' + param['value'] + ',';
                });

                // remove the last comma in the params
                url = url.slice(0, -1) + '%7D';

                await API.get(url, {headers: {Authorization: token, 'Sort-Type': sortOrder,'Sort-Criteria': state.sorted[0]['id']}})
                    .then(response => {
                        handleSuccessApiResponse(url, response, state);
                    }).catch(err => {
                        setTableLoader(false);
                        if (err.message.indexOf('404') >= 0) {
                            const msg = "No result found!";
                            setAlert("Error", "Error", msg);
                        } else {
                            setAlert("Error", "Error", "Something went wrong!");
                        }
                    });
            } else if (tableFilterUrl !== null) {
                console.log('both search from filter pane ' + '& sort = ' + state.sorted[0]['id']);

                setTableLoader(true);

                let sortOrder = 'ASC';
                if (state.sorted[0]['desc'])
                    sortOrder = 'DESC';

                let url = tableBaseUrl + state.page + tableFilterUrl;

                await API.get(url, {headers: {Authorization: token, 'Sort-Type': sortOrder,'Sort-Criteria': state.sorted[0]['id']}})
                    .then(response => {
                        handleSuccessApiResponse(url, response, state);
                    }).catch(err => {
                        setTableLoader(false);
                        if (err.message.indexOf('404') >= 0) {
                            const msg = "No result found!";
                            setAlert("Error", "Error", msg);
                        } else {
                            setAlert("Error", "Error", "Something went wrong!");
                        }
                    });

            } else {
                console.log('only sort = ' + state.sorted[0]['id']);

                setTableLoader(true);

                let sortOrder = 'ASC';
                if (state.sorted[0]['desc'])
                    sortOrder = 'DESC';

                const url =  tableBaseUrl + state.page + (tableFilterUrl || '');

                await API.get(url, {headers: {Authorization: token, 'Sort-Type': sortOrder, 'Sort-Criteria': state.sorted[0]['id']}})
                    .then(response => {
                        handleSuccessApiResponse(url, response, state);
                    }).catch(er => {
                        handleErrorApiResponse(er);
                        setTableLoader(false);
                    });
            }
        } else {
            if (state.filtered.length !== undefined && state.filtered.length !== 0) {
                console.log('only search = ' + state.filtered[0]['id']);

                setTableLoader(true);

                let sortOrder = 'ASC';
                if (state.sorted.length !== 0 && state.sorted[0]['desc'])
                    sortOrder = 'DESC';

                let url = tableBaseUrl + state.page + '&search=%7B';

                state.filtered.forEach(param => {
                    if (param['id'] == 'ltiToolProfile.code') {
                        url += 'code%3A' + param['value'] + ',';
                    } else {
                        url += param['id'] + ':' + param['value'] + ',';
                    }
                });

                // remove the last comma in the params
                url = url.slice(0, -1) + '%7D';

                await API.get(url, {headers: {Authorization: token, 'Sort-Type': sortOrder}})
                    .then(response => {
                        handleSuccessApiResponse(url, response, state);
                    })
                    .catch(er => {
                        if (er.message.includes('404')) {
                            setAlert("Error", "Sorry", "No result found!");
                        } else {
                            setAlert("Error", "Error", "Something went wrong!");
                        }
                        setTableLoader(false);
                    });
            } else {
                console.log('load normal table data');

                setTableLoader(true);

                let url;
                if (tableFilterUrl === null) {
                    url = tableBaseUrl + state.page;
                    await loadNormalTableData(state, url, token);
                } else if (tableFilterUrl.tpiAuditParam !== undefined) {
                    url = tableBaseUrl + state.page;
                    await API.post(url, {
                        'tpiAuditParam': tableFilterUrl.tpiAuditParam
                    }, {headers: {Authorization: token}})
                        .then(response => {
                            handleSuccessApiResponse(url, response, state);
                        })
                        .catch(er => {
                            handleErrorApiResponse(er);
                            setTableLoader(false);
                        });
                } else {
                    url = tableBaseUrl + state.page + tableFilterUrl
                    await loadNormalTableData(state, url, token);
                }
            }
        }
    };

    const loadNormalTableData = async (state, url, token) => {
        await API.get(url, {headers: {Authorization: token}})
            .then(response => {
                handleSuccessApiResponse(url, response, state);
            })
            .catch(er => {
                handleErrorApiResponse(er);
                setTableLoader(false);
            });
    };

    const handleSuccessApiResponse = async (url, response, state) => {
        setTableData(fetchTableData(response.data));

        setTableLoader(false);

        const totalResults = response.headers['total-results'];
        if (totalResults !== undefined && totalResults !== null && totalResults !== '') {
            setTotPageCount(totalResults, pageSize);
        }
    };

    const handleErrorApiResponse = (error) => {
        if (error.response !== undefined && error.response.status === 404) {
            setAlert("Error", "Error", "There are no any results found!");
        } else if (error.response !== undefined && error.response.status === 401) {
            localStorage.removeItem('tmcUser')
            props.routerProps.history.push('/');
        } else {
            setAlert("Error", "Error", "Something went wrong");
        }
    };

    const setAlert = (alertType, alertTitle, alertMessage) => {
        EventEmitter.emit('setAlert', {
            openAlert: true,
            type: alertType,
            isDisplayAlertIcon: true,
            alertMessage: alertMessage,
            alertTitle: alertTitle
        });
    };

    /**
     * set total page count in the pagination
     * @param totalResults - total number of results
     */
    const setTotPageCount = (totalResults, rowsPerPage) => {
        const total = parseInt(totalResults, 10);
        if (!Number.isFinite(total)) {
            return;
        }
        const pages = total === 0 ? 1 : Math.ceil(total / rowsPerPage);
        setTotalPageCount(pages);
    };

    /**
     * set onclick function to a table row
     * @param rowInfo
     * @param column
     * @returns {{onClick: onClick, title: *}|{}}
     */
    const addCustomPropsToTds = (rowInfo, column) => {
        let isClickable = true;

        if (rowInfo !== undefined) {
            if (isClickable)
                return {
                    title: rowInfo.row['_original'][column['id']],
                    onClick: () => {
                        const path = table.rowClickUrl + rowInfo.original[table.defaultId];
                        props.routerProps.history.push(path)
                    }
                };
            else
                return {};
        } else
            return {};
    };

    /**
     * set id to a table row
     * @param rowInfo
     * @returns {{id: *}|{}}
     */
    const addCustomPropsToTrs = (rowInfo) => {
        if (rowInfo !== undefined) {
            return {
                id: rowInfo.original.id
            };
        } else
            return {};
    };

    return(
        <div className={'table-container'}>
            <ReactTable
                ref={reactTable}
                data={tableData}
                columns={tableColumns}
                previousText={<Icon name={"back-18"}/>}
                nextText={<Icon name={"next-18"}/>}
                defaultPageSize={pageSize}
                defaultSorted={table.defaultSorted || []}
                loading={tableLoader}
                pages={totalPageCount}
                manual
                filterable={table.isFilterable}
                sortable={table.isSortable}
                getProps={() => addCustomPropsToTds}
                showPageSizeOptions={false}
                showPageJump={true}
                onFetchData={state => onFetchData(state)}
                getTdProps={(state, rowInfo, column) => {
                    return addCustomPropsToTds(rowInfo, column)
                }}
                getTrProps={(state, rowInfo) => {
                    return addCustomPropsToTrs(rowInfo)
                }}
            />
        </div>
    )
};

const setInitialTableColumns = (table) => {
    let counter = 0;
    const initialTableColumns = [];
    table.tableHeaders.forEach(tit => {
        const columnDataAlignment = table.columnDataAlignment[counter];
        initialTableColumns.push({
            Header: tit,
            accessor: table.tableAttributes[counter],
            show: table.headerVisibility[counter],
            filterable: table.columnFilters[counter],
            // width: table.columnWidths[counter],
            Cell: row => <div style={{ textAlign: columnDataAlignment }}>{row.value}</div>,
            Filter: ({ filter, onChange }) => {
                return (
                    <input aria-label="column search field" style={{width: '100%'}}
                           onKeyPress={event => {
                               const newValue = event.target.value;
                               console.log("newValue",newValue)
                               if (event.keyCode === 13 || event.which === 13) {
                                   onChange(event.target.value);
                               }
                           }}
                    />
                )},
        });
        counter++;
    });
    return initialTableColumns;
};

const setInitialCheckedItems = (table) => {
    let counter = 0;
    const initialCheckedItems = {};
    table.tableHeaders.forEach(tit => {
        initialCheckedItems[tit] = table.headerVisibility[counter];
        counter++;
    });
    return initialCheckedItems;
};

export default Table;
